import React from 'react';
import type { ImageWithTags } from './types';

export interface ImageGridSectionProps {
  filteredCount: number;
  totalCount: number;
  onRefresh: () => void;
  refreshDisabled: boolean;
  useInfiniteScroll: boolean;
  setUseInfiniteScroll: (v: boolean) => void;
  filterVersion: number;
  imageContainerRef: React.RefObject<HTMLDivElement | null>;
  displayedImages: ImageWithTags[];
  presignedUrls: Map<string, string>;
  selectedImages: Set<string>;
  onImageSelect: (s3Url: string, index: number, event: React.MouseEvent) => void;
  onImageDoubleClick: (s3Url: string, presignedUrl: string) => void;
  onImageDimensionsLoaded?: (s3Url: string, width: number, height: number) => void;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (v: number) => void;
  startIndex: number;
  endIndex: number;
}

const ImageGridSection: React.FC<ImageGridSectionProps> = ({
  filteredCount,
  totalCount,
  onRefresh,
  refreshDisabled,
  useInfiniteScroll,
  setUseInfiniteScroll,
  filterVersion,
  imageContainerRef,
  displayedImages,
  presignedUrls,
  selectedImages,
  onImageSelect,
  onImageDoubleClick,
  onImageDimensionsLoaded,
  totalPages,
  currentPage,
  setCurrentPage,
  startIndex,
  endIndex,
}) => (
  <>
    {/* Row 1: Title left, Refresh + Infinite scroll top right */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
      <h3 style={{ margin: 0 }}>
        Images {filteredCount !== totalCount ? `(${filteredCount} of ${totalCount})` : `(${totalCount})`}
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshDisabled}
          style={{
            padding: '0.4rem 0.75rem',
            fontSize: '0.9rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            background: '#fff',
            cursor: refreshDisabled ? 'not-allowed' : 'pointer',
          }}
          title="Re-run query to load updated tags"
        >
          {refreshDisabled ? 'Refreshing...' : 'Refresh'}
        </button>
        <label style={{ fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={useInfiniteScroll}
            onChange={(e) => setUseInfiniteScroll(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Infinite Scroll
        </label>
      </div>
    </div>

    {/* Pagination controls - only show if not using infinite scroll */}
    {!useInfiniteScroll && totalPages > 1 && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.5rem' }}>
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          style={{ padding: '0.5rem 1rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages} (Showing {startIndex + 1}-{Math.min(endIndex, filteredCount)} of {filteredCount} images)
        </span>
        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          style={{ padding: '0.5rem 1rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
        >
          Next
        </button>
      </div>
    )}
    {useInfiniteScroll && (
      <div style={{ marginBottom: '1rem', padding: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
        Showing {displayedImages.length} of {filteredCount} images
        {displayedImages.length < filteredCount && ' (scroll to load more)'}
      </div>
    )}
    <div
      key={`grid-${filterVersion}`}
      ref={imageContainerRef as React.RefObject<HTMLDivElement>}
      className="image-grid"
      style={useInfiniteScroll ? { maxHeight: '600px', overflowY: 'auto', overflowX: 'hidden' } : {}}
    >
      {displayedImages.map((img, idx) => {
        const actualIndex = useInfiniteScroll ? idx : startIndex + idx;
        const presignedUrl = presignedUrls.get(img.s3_url);
        const isSelected = selectedImages.has(img.s3_url);
        return (
          <div
            key={img.s3_url}
            className={`image-item ${isSelected ? 'selected' : ''}`}
            onClick={(e) => onImageSelect(img.s3_url, actualIndex, e)}
            onDoubleClick={() => {
              if (presignedUrl) onImageDoubleClick(img.s3_url, presignedUrl);
            }}
          >
            {presignedUrl ? (
              <img
                src={presignedUrl}
                alt={img.s3_url}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                    onImageDimensionsLoaded?.(img.s3_url, el.naturalWidth, el.naturalHeight);
                  }
                }}
              />
            ) : (
              <div>Loading...</div>
            )}
          </div>
        );
      })}
    </div>
  </>
);

export default ImageGridSection;
